import {
  FocusEvent,
  ReactElement,
  ReactNode,
  useEffect,
  useState,
} from 'react';
import {
  Autocomplete,
  Chip,
  Link,
  Stack,
  TextField,
  Typography,
} from '@rossum/ui/material';
import { useIntl } from '../ComponentsContext';

// Subset of react-hook-form's `ControllerRenderProps` / `ControllerFieldState`
// (for the `{ to, cc, bcc }` form) that this component actually uses, hardcoded
// so the library doesn't need to depend on react-hook-form just for these types.
type Field = Partial<{
  onChange: (...event: unknown[]) => void;
  onBlur: () => void;
  value: string[];
}>;

type FieldState = Partial<{
  error: { message?: string };
}>;

const EmailTypeahead = ({
  label,
  endAdornment,
  options,
  field,
  fieldState,
  validate,
  disabled,
}: {
  label: string;
  endAdornment?: ReactNode;
  options: string[] | undefined;
  field: Field;
  fieldState?: FieldState;
  validate: (email: string) => boolean;
  disabled?: boolean;
}) => {
  const [inputValue, setInputValue] = useState('');

  const handleOnBlur = (e: FocusEvent<HTMLInputElement>) => {
    const { value } = e.target;
    if (!value) return;

    field.onChange?.([...(field.value ?? []), value]);
    field.onBlur?.();
  };

  return (
    <Autocomplete
      {...field}
      onChange={(_e, v) => {
        if (v) {
          field.onChange?.(v ?? '');
        }
      }}
      options={options ?? []}
      clearOnBlur
      onBlur={handleOnBlur}
      disabled={disabled}
      renderInput={params => (
        <TextField
          {...params}
          autoFocus
          label={label}
          size="small"
          error={Boolean(fieldState?.error)}
          helperText={fieldState?.error ? fieldState.error.message : ''}
          InputProps={{
            ...params.InputProps,
            style: {
              paddingRight: 16 + (endAdornment ? 20 : 0),
            },
            endAdornment: (
              <Stack direction="row" alignItems="center" spacing={0.5}>
                {params.InputProps.endAdornment}
                {endAdornment}
              </Stack>
            ),
          }}
        />
      )}
      disablePortal
      freeSolo
      multiple
      open={!!inputValue.length}
      renderOption={(props, option) => (
        <li {...props}>
          <Typography fontSize="small">{option}</Typography>
        </li>
      )}
      renderTags={(_value, getTagProps) => {
        return _value.map((chip, i) => (
          <Chip
            {...getTagProps({ index: i })}
            color={validate(chip) ? 'default' : 'error'}
            label={chip}
            key={`chip-${chip}`}
          />
        ));
      }}
      inputValue={inputValue}
      onInputChange={(_, newValue) => setInputValue(newValue)}
    />
  );
};

// Will this be re-usable in Email Response form?
export const EmailRecipients = ({
  suggestedRecipientEmails,
  to,
  cc,
  bcc,
  validate,
  disabled,
  createSuggestVariablesComponent,
}: {
  suggestedRecipientEmails: string[] | undefined;
  to: { field: Field; fieldState?: FieldState };
  cc: { field: Field; fieldState?: FieldState };
  bcc: { field: Field; fieldState?: FieldState };
  validate: (s: string) => boolean;
  disabled?: boolean;
  createSuggestVariablesComponent?: (
    fieldType: 'to' | 'cc' | 'bcc'
  ) => ReactElement;
}) => {
  const [ccVisible, setCcVisible] = useState(false);
  const [bccVisible, setBccVisible] = useState(false);

  useEffect(() => {
    if (cc.field.value && cc.field.value.length > 0) {
      setCcVisible(true);
    }
  }, [cc.field.value]);

  useEffect(() => {
    if (bcc.field.value && bcc.field.value.length > 0) {
      setBccVisible(true);
    }
  }, [bcc.field.value]);

  const intl = useIntl();

  return (
    <>
      <EmailTypeahead
        {...to}
        validate={validate}
        options={suggestedRecipientEmails}
        disabled={disabled}
        label={intl.formatMessage({
          id: 'components.emailResponseForm.fields.edit.to.label',
        })}
        endAdornment={
          <Stack direction="row" alignItems="center">
            <Link
              variant="body2"
              color={ccVisible ? 'primary' : 'inherit'}
              sx={{
                mx: 1,
                cursor: 'pointer',
              }}
              onClick={() => {
                if (ccVisible === true) {
                  cc.field.onChange?.([]);
                }
                setCcVisible(!ccVisible);
              }}
            >
              {intl.formatMessage({
                id: 'components.emailResponseForm.fields.edit.buttons.copy.label',
              })}
            </Link>
            <Link
              variant="body2"
              color={bccVisible ? 'primary' : 'inherit'}
              sx={{
                mx: 1,
                cursor: 'pointer',
              }}
              onClick={() => {
                if (bccVisible === true) {
                  bcc.field.onChange?.([]);
                }
                setBccVisible(!bccVisible);
              }}
            >
              {intl.formatMessage({
                id: 'components.emailResponseForm.fields.edit.buttons.blindCopy.label',
              })}
            </Link>
            {createSuggestVariablesComponent?.('to')}
          </Stack>
        }
      />
      {ccVisible && (
        <EmailTypeahead
          {...cc}
          validate={validate}
          options={suggestedRecipientEmails}
          disabled={disabled}
          endAdornment={createSuggestVariablesComponent?.('cc')}
          label={intl.formatMessage({
            id: 'components.emailResponseForm.fields.edit.cc.label',
          })}
        />
      )}
      {bccVisible && (
        <EmailTypeahead
          {...bcc}
          validate={validate}
          options={suggestedRecipientEmails}
          disabled={disabled}
          endAdornment={createSuggestVariablesComponent?.('bcc')}
          label={intl.formatMessage({
            id: 'components.emailResponseForm.fields.edit.bcc.label',
          })}
        />
      )}
    </>
  );
};
